import React, { useEffect, useMemo, useState } from 'react';
import {
   Button,
   Checkbox,
   Dimmer,
   Form,
   Header,
   Icon,
   Input,
   Loader,
   Message,
   Modal,
   Segment,
   Table
} from 'semantic-ui-react';
import { toast } from 'react-toastify';
import Excel from 'exceljs';
import * as Api from '../../utils/API';

const parseMarkup = (value) => {
   if (value === null || value === undefined || value === '') {
      return null;
   }

   const parsed = Number(String(value).replace(',', '.'));
   return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const normalizeMarkupInput = (value) => {
   return String(value || '').replace(/[^\d,.]/g, '');
};

const formatDate = (date = new Date()) => date.toLocaleDateString('pt-BR');
const espacoClienteBaseUrl = 'https://cliente.esplane.com.br';

const toastOptions = {
   position: "top-right",
   autoClose: 4000,
   hideProgressBar: false,
   closeOnClick: true,
   draggable: true,
   progress: undefined,
   style: {
      background: "#fff",
      color: "#000",
   },
};

const showCopyToast = (type, message) => {
   toast[type](
      <div>
         <p style={{ margin: 0 }}>{message}</p>
         <small style={{ margin: 0 }}>Clique aqui para fechar.</small>
      </div>,
      toastOptions
   );
};

const buildWorkbook = async (rows, parceiro, vendedor, dataPlanilha) => {
   const workbook = new Excel.Workbook();
   const worksheet = workbook.addWorksheet('Tabela');
   const descricaoTabelaPreco = parceiro?.descr_tabela_preco || parceiro?.descrTabelaPreco || 'Sem descriÃ§Ã£o';
   const tituloTabelaPreco = `Tabela de Preço (${parceiro?.codtab || '-'} - ${descricaoTabelaPreco})`;
   const thinBlackBorder = {
      top: { style: 'thin', color: { argb: 'FF000000' } },
      left: { style: 'thin', color: { argb: 'FF000000' } },
      bottom: { style: 'thin', color: { argb: 'FF000000' } },
      right: { style: 'thin', color: { argb: 'FF000000' } }
   };

   worksheet.columns = [
      { header: 'SKU', key: 'codprod', width: 12 },
      { header: 'PRODUTO', key: 'produto', width: 42 },
      { header: 'PREÇO_VENDA', key: 'final_cx_markup', width: 16 },
      { header: 'UNIDADE_VENDA', key: 'unidade_venda', width: 15 },
      { header: 'RENDIMENTO', key: 'rendimento', width: 22 },
      { header: 'PREÇO', key: 'final_m2_markup', width: 16 },
      { header: 'UNIDADE', key: 'unidade', width: 10 }
   ];

   worksheet.spliceRows(1, 0, [], [], [], []);
   worksheet.pageSetup = {
      paperSize: 9,
      orientation: 'portrait',
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 0,
      horizontalCentered: true,
      margins: {
         left: 0.25,
         right: 0.25,
         top: 0.35,
         bottom: 0.35,
         header: 0.2,
         footer: 0.2
      }
   };

   worksheet.mergeCells('A1:G1');
   worksheet.getCell('A1').value = tituloTabelaPreco;
   worksheet.getCell('A1').font = { bold: true, size: 14, color: { argb: 'FFFFFFFF' } };
   worksheet.getCell('A1').fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF1884AF' }
   };
   worksheet.getCell('A1').alignment = { vertical: 'middle', horizontal: 'center' };

   worksheet.getCell('A2').value = 'Parceiro';
   worksheet.getCell('B2').value = `${parceiro?.codparc || ''} - ${parceiro?.razaosocial || ''}`;
   worksheet.getCell('D2').value = 'Vendedor';
   worksheet.getCell('E2').value = `${vendedor?.codvend || ''} - ${vendedor?.apelido || ''}`;
   worksheet.getCell('F2').value = 'Data';
   worksheet.getCell('G2').value = dataPlanilha;

   ['A2', 'D2', 'F2'].forEach((cellRef) => {
      worksheet.getCell(cellRef).font = { bold: true };
      worksheet.getCell(cellRef).fill = {
         type: 'pattern',
         pattern: 'solid',
         fgColor: { argb: 'FFECECEC' }
      };
   });

   worksheet.getRow(4).height = 6;

   rows.forEach((item) => {
      worksheet.addRow({
         codprod: item.codprod,
         produto: item.produto,
         final_cx_markup: Number(item.final_cx_markup || 0),
         unidade_venda: item.unidade_venda,
         rendimento: item.rendimento,
         final_m2_markup: Number(item.final_m2_markup || 0),
         unidade: item.unidade
      });
   });

   worksheet.views = [{ state: 'frozen', ySplit: 5 }];
   worksheet.autoFilter = {
      from: 'A5',
      to: `G${rows.length + 5}`
   };

   worksheet.getRow(5).eachCell((cell) => {
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      cell.fill = {
         type: 'pattern',
         pattern: 'solid',
         fgColor: { argb: 'FF1884AF' }
      };
      cell.alignment = { vertical: 'middle', horizontal: 'center' };
   });

   worksheet.getColumn('final_cx_markup').numFmt = '"R$" #,##0.00';
   worksheet.getColumn('final_m2_markup').numFmt = '"R$" #,##0.00';

   worksheet.eachRow((row) => {
      row.eachCell({ includeEmpty: true }, (cell) => {
         cell.border = thinBlackBorder;
         cell.alignment = {
            vertical: 'middle',
            horizontal: cell.alignment?.horizontal || 'center',
            wrapText: true
         };
      });
   });

   worksheet.getColumn('produto').alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };

   const buffer = await workbook.xlsx.writeBuffer();
   const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
   const url = window.URL.createObjectURL(blob);
   const link = document.createElement('a');
   link.href = url;
   link.download = `tabela-precos-revenda-${parceiro?.codparc}.xlsx`;
   document.body.appendChild(link);
   link.click();
   document.body.removeChild(link);
   window.URL.revokeObjectURL(url);
};

function TabelaPrecoRevendaModal({ open, onClose, parceiro, codvend, vendedor }) {
   const [loading, setLoading] = useState(false);
   const [saving, setSaving] = useState(false);
   const [generating, setGenerating] = useState(false);
   const [categorias, setCategorias] = useState([]);
   const [markupGeral, setMarkupGeral] = useState('');
   const [markups, setMarkups] = useState({});
   const [selecionadas, setSelecionadas] = useState({});
   const [categoriasConfiguradas, setCategoriasConfiguradas] = useState({});
   const [error, setError] = useState('');
   const [copyingLink, setCopyingLink] = useState(false);
   const dataPlanilha = formatDate();

   useEffect(() => {
      if (!open || !parceiro?.codparc) {
         return;
      }

      setLoading(true);
      setError('');

      Promise.all([
         Api.getTabelaPrecoRevendaCategorias(),
         Api.getTabelaPrecoRevendaConfig({ codparc: parceiro.codparc })
      ]).then(([categoriasResult, configResult]) => {
         const listaCategorias = Array.isArray(categoriasResult) ? categoriasResult : [];
         const listaConfig = Array.isArray(configResult) ? configResult : [];
         const geral = listaConfig.find((item) => Number(item.idcategoria) === 0);
         const markupsConfig = {};
         const selecionadasConfig = {};
         const configuradasConfig = {};

         listaCategorias.forEach((item) => {
            selecionadasConfig[item.idcategoria] = false;
         });

         listaConfig.forEach((item) => {
            if (Number(item.idcategoria) > 0) {
               markupsConfig[item.idcategoria] = String(item.markup || '').replace('.', ',');
               selecionadasConfig[item.idcategoria] = true;
               configuradasConfig[item.idcategoria] = true;
            }
         });

         setCategorias(listaCategorias);
         setMarkupGeral(geral ? String(geral.markup || '').replace('.', ',') : '');
         setMarkups(markupsConfig);
         setSelecionadas(selecionadasConfig);
         setCategoriasConfiguradas(configuradasConfig);
      }).catch(() => {
         setError('Nao foi possivel carregar as configuracoes da tabela.');
      }).finally(() => {
         setLoading(false);
      });
   }, [open, parceiro]);

   const categoriasPorGrupo = useMemo(() => {
      return categorias.reduce((acc, item) => {
         const grupo = item.grupo || 'Sem grupo';
         if (!acc[grupo]) {
            acc[grupo] = [];
         }
         acc[grupo].push(item);
         return acc;
      }, {});
   }, [categorias]);

   const totalSelecionadas = useMemo(() => {
      return Object.keys(selecionadas).filter((key) => selecionadas[key]).length;
   }, [selecionadas]);

   const handleCopyEspacoCliente = async (e) => {
      e.preventDefault();

      if (copyingLink) {
         return;
      }

      setCopyingLink(true);

      try {
         const result = await Api.getWhatsappToken({ codparc: parceiro?.codparc });
         const response = Array.isArray(result) ? result[0] : result;
         const token = response?.token;

         if (response?.status === 'Erro' || !token) {
            throw new Error(response?.message || 'Token do Espaco Cliente nao encontrado.');
         }

         await navigator.clipboard.writeText(`${espacoClienteBaseUrl}/${encodeURIComponent(token)}`);
         showCopyToast('success', 'Link copiado com sucesso!');
      } catch (err) {
         console.error('Erro ao copiar link do Espaco Cliente:', err);
         showCopyToast('error', 'Erro ao copiar o link.');
      } finally {
         setCopyingLink(false);
      }
   };

   const toggleGrupo = (grupo) => {
      const itens = categoriasPorGrupo[grupo] || [];
      const todosMarcados = itens.every((item) => selecionadas[item.idcategoria]);
      const next = { ...selecionadas };

      if (todosMarcados) {
         itens.forEach((item) => {
            next[item.idcategoria] = false;
         });
      } else {
         itens.forEach((item) => {
            next[item.idcategoria] = true;
         });
      }

      setSelecionadas(next);
   };

   const buildConfigPayload = () => {
      const markupGeralNumber = parseMarkup(markupGeral);
      const categoriasPayload = categorias
         .filter((item) => selecionadas[item.idcategoria] || categoriasConfiguradas[item.idcategoria])
         .map((item) => ({
            idcategoria: item.idcategoria,
            selected: !!selecionadas[item.idcategoria],
            markup: parseMarkup(markups[item.idcategoria]) || markupGeralNumber || 0
         }));

      return {
         codparc: parceiro.codparc,
         markupGeral: markupGeralNumber,
         categorias: categoriasPayload
      };
   };

   const saveConfig = () =>
      Api.saveTabelaPrecoRevendaConfig(buildConfigPayload()).then((result) => {
         const response = Array.isArray(result) ? result[0] : result;
         if (response?.status === 'Erro') {
            throw new Error(response.message || 'Nao foi possivel salvar as configuracoes.');
         }
         return response;
      });

   const handleSave = () => {
      setSaving(true);
      setError('');

      saveConfig().then(() => {
         const nextConfiguradas = { ...categoriasConfiguradas };
         categorias.forEach((item) => {
            if (selecionadas[item.idcategoria]) {
               nextConfiguradas[item.idcategoria] = true;
            } else {
               delete nextConfiguradas[item.idcategoria];
            }
         });
         setCategoriasConfiguradas(nextConfiguradas);
         toast.success('Configuracoes salvas com sucesso.');
      }).catch((err) => {
         setError(err.message || 'Nao foi possivel salvar as configuracoes.');
      }).finally(() => {
         setSaving(false);
      });
   };

   const handleGenerate = () => {
      const categoriasSelecionadas = Object.keys(selecionadas)
         .filter((key) => selecionadas[key])
         .map((key) => Number(key));

      if (categoriasSelecionadas.length === 0) {
         toast.warn('Selecione pelo menos uma categoria.');
         return;
      }

      setGenerating(true);
      setError('');

      saveConfig().then(() => Api.gerarTabelaPrecoRevenda({
         codparc: parceiro.codparc,
         codvend,
         categorias: categoriasSelecionadas
      })).then((result) => {
         if (result?.status === 'Erro') {
            setError(result.message || 'Nao foi possivel gerar a tabela.');
            return;
         }

         const rows = result?.dados || [];
         if (rows.length === 0) {
            toast.warn('Nenhum produto encontrado para as categorias selecionadas.');
            return;
         }

         buildWorkbook(rows, parceiro, vendedor, dataPlanilha).catch(() => {
            setError('Nao foi possivel montar o arquivo Excel.');
         });
      }).catch((err) => {
         setError(err.message || 'Nao foi possivel gerar a tabela.');
      }).finally(() => {
         setGenerating(false);
      });
   };

   return (
      <Modal open={open} onClose={onClose} closeOnDimmerClick={false} size="large">
         <Modal.Header style={{ backgroundColor: '#dfdfdf', padding: '1em 1.5em' }}>
            <Header as="h3" style={{ margin: 0 }}>
               <Icon name="file excel outline" color="green" />
               <Header.Content>
                  Tabela de Precos da Revenda
                  <Header.Subheader>
                     {parceiro?.codparc} - {parceiro?.razaosocial}
                  </Header.Subheader>
               </Header.Content>
            </Header>
         </Modal.Header>

         <Modal.Content scrolling style={{ padding: '1em 1.5em' }}>
            <Dimmer active={loading} inverted>
               <Loader>Carregando configuracoes...</Loader>
            </Dimmer>

            {error && <Message negative content={error} />}

            <Segment style={{ boxShadow: 'none', border: '1px solid #d4d4d5', marginBottom: '1em', padding: '0.85em 1em' }}>
               <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1em', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1.5em', flexWrap: 'wrap' }}>
                     <div>
                        <div style={{ color: '#666', fontSize: '11px', textTransform: 'uppercase', fontWeight: 700 }}>Vendedor</div>
                        <div style={{ fontWeight: 600 }}>{vendedor?.codvend || codvend} - {vendedor?.apelido || 'Selecionado'}</div>
                     </div>
                     <div>
                        <div style={{ color: '#666', fontSize: '11px', textTransform: 'uppercase', fontWeight: 700 }}>Data da planilha</div>
                        <div style={{ fontWeight: 600 }}>{dataPlanilha}</div>
                     </div>
                     <div>
                        <div style={{ color: '#666', fontSize: '11px', textTransform: 'uppercase', fontWeight: 700 }}>Categorias selecionadas</div>
                        <div style={{ fontWeight: 600 }}>{totalSelecionadas} de {categorias.length}</div>
                     </div>
                     <div>
                        <div style={{ color: '#666', fontSize: '11px', textTransform: 'uppercase', fontWeight: 700 }}>Tabela de preco</div>
                        <div style={{ fontWeight: 600 }}>
                           {parceiro?.codtab || '-'} - {parceiro?.descr_tabela_preco || parceiro?.descrTabelaPreco || 'Sem descricao'}
                        </div>
                     </div>
                  </div>

                  <Button
                     as="a"
                     href="#"
                     size="small"
                     loading={copyingLink}
                     disabled={copyingLink}
                     onClick={handleCopyEspacoCliente}
                     style={{
                        alignSelf: 'center',
                        backgroundColor: '#2185D0',
                        border: '1px solid #1678c2',
                        boxShadow: '0 2px 6px rgba(33, 133, 208, 0.25)',
                        color: '#fff',
                        fontWeight: 700,
                        margin: 0,
                        whiteSpace: 'nowrap'
                     }}
                  >
                     <Icon name="copy outline" style={{ color: '#fff' }} />
                     Copiar Link
                  </Button>

                  <Form.Field style={{ margin: 0, minWidth: '190px' }}>
                     <label style={{ fontWeight: 700 }}>Markup Geral (%)</label>
                     <Input
                        fluid
                        size="small"
                        value={markupGeral}
                        placeholder="Ex.: 10,5"
                        onChange={(e) => setMarkupGeral(normalizeMarkupInput(e.target.value))}
                     />
                  </Form.Field>
               </div>
            </Segment>

            <Segment basic style={{ padding: 0, margin: 0 }}>
               <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '.5em' }}>
                  <div style={{ color: '#555', fontSize: '12px' }}>
                     Selecione as categorias desejadas para gerar a tabela de preÃ§os.
                   
                  </div>
               </div>
            </Segment>

            <Table celled compact="very" size="small" style={{ marginTop: 0 }}>
               <Table.Header>
                  <Table.Row>
                     <Table.HeaderCell width={1}>Usar</Table.HeaderCell>
                     <Table.HeaderCell width={4}>Grupo</Table.HeaderCell>
                     <Table.HeaderCell width={7}>Categoria</Table.HeaderCell>
                     <Table.HeaderCell width={3}>Markup (%)</Table.HeaderCell>
                  </Table.Row>
               </Table.Header>
               <Table.Body>
                  {Object.keys(categoriasPorGrupo).map((grupo) => (
                     <React.Fragment key={grupo}>
                        <Table.Row active>
                           <Table.Cell colSpan={4} style={{ textAlign: 'left', background: '#e8eef2', padding: '.45em .75em' }}>
                              <button
                                 type="button"
                                 onClick={() => toggleGrupo(grupo)}
                                 style={{
                                    border: 'none',
                                    background: 'transparent',
                                    color: '#1884af',
                                    fontWeight: 700,
                                    cursor: 'pointer',
                                    padding: 0
                                 }}
                              >
                                 <Icon name="object group outline" />
                                 {grupo}
                              </button>
                           </Table.Cell>
                        </Table.Row>
                        {categoriasPorGrupo[grupo].map((item) => (
                           <Table.Row key={item.idcategoria}>
                              <Table.Cell textAlign="center">
                                 <Checkbox
                                    checked={!!selecionadas[item.idcategoria]}
                                    onChange={(e, { checked }) => {
                                       setSelecionadas({
                                          ...selecionadas,
                                          [item.idcategoria]: checked
                                       });
                                    }}
                                 />
                              </Table.Cell>
                              <Table.Cell style={{ fontWeight: 600 }}>{item.grupo}</Table.Cell>
                              <Table.Cell>{item.categoria} ({item.idcategoria})</Table.Cell>
                              <Table.Cell>
                                 <Input
                                    fluid
                                    size="mini"
                                    value={markups[item.idcategoria] || ''}
                                    placeholder="Markup diferente do Geral"
                                    onChange={(e) => setMarkups({
                                       ...markups,
                                       [item.idcategoria]: normalizeMarkupInput(e.target.value)
                                    })}
                                 />
                              </Table.Cell>
                           </Table.Row>
                        ))}
                     </React.Fragment>
                  ))}
               </Table.Body>
            </Table>
         </Modal.Content>

         <Modal.Actions>
            <Button onClick={onClose} negative icon="close" content="FECHAR" />
            <Button
               basic
               color="blue"
               icon="save"
               content="Salvar Configuracoes"
               loading={saving}
               disabled={loading || saving || generating}
               onClick={handleSave}
            />
            <Button
               color="green"
               icon="file excel outline"
               content="Gerar Tabela"
               loading={generating}
               disabled={loading || saving || generating}
               onClick={handleGenerate}
            />
         </Modal.Actions>
      </Modal>
   );
}

export default TabelaPrecoRevendaModal;
